// pages/sites/SiteCreate.js

// pages/sites/SiteCreate.js

import React, { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import api from '../../services/api'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { useGlobal } from '../../context/GlobalContext'
import '../../assets/css/SiteCreate.css'

function SiteCreate() {
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [lat, setLat] = useState(0)
  const [lng, setLng] = useState(0)
  const [isOurSite, setIsOurSite] = useState(false)
  const [predictions, setPredictions] = useState([])
  const [mapRef, setMapRef] = useState(null)
  const navigate = useNavigate()
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places']
  })
  const { showLoading, hideLoading, showMessage } = useGlobal()

  const onLoadMap = useCallback(map => {
    setMapRef(map)
  }, [])

  const onUnmountMap = useCallback(() => {
    setMapRef(null)
  }, [])

  const getUserLocation = () => new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => {
        setLat(pos.coords.latitude)
        setLng(pos.coords.longitude)
        resolve()
      }, () => {
        showMessage('Unable to get location', 'error')
        reject()
      })
    } else {
      showMessage('Geolocation is not supported by this browser', 'error')
      reject()
    }
  })

  const reverseGeocode = async (latitude, longitude) => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
    const res = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${key}`)
    if (res.data.results && res.data.results.length) {
      setAddress(res.data.results[0].formatted_address)
    }
  }

  const fetchCurrentLocation = async () => {
    showLoading()
    try {
      await getUserLocation()
      if (mapRef) mapRef.panTo({ lat, lng })
      await reverseGeocode(lat, lng)
    } catch {}
    hideLoading()
  }

  const handleMarkerDragEnd = async e => {
    const newLat = e.latLng.lat()
    const newLng = e.latLng.lng()
    setLat(newLat)
    setLng(newLng)
    showLoading()
    try {
      await reverseGeocode(newLat, newLng)
    } catch {
      showMessage('Error fetching address', 'error')
    }
    hideLoading()
  }

  const handleAddressChange = async e => {
    setAddress(e.target.value)
    if (e.target.value.length > 2) {
      showLoading()
      try {
        const res = await api.get(`/site/autocomplete?input=${encodeURIComponent(e.target.value)}`)
        setPredictions(res.data.predictions || [])
      } catch {
        showMessage('Error fetching address suggestions', 'error')
      }
      hideLoading()
    } else {
      setPredictions([])
    }
  }

  const handlePredictionClick = async prediction => {
    setAddress(prediction.description)
    setPredictions([])
    showLoading()
    try {
      const res = await api.get(`/site/placedetails?place_id=${prediction.place_id}`)
      if (
        res.data &&
        res.data.result &&
        res.data.result.geometry &&
        res.data.result.geometry.location
      ) {
        const newLat = res.data.result.geometry.location.lat
        const newLng = res.data.result.geometry.location.lng
        setLat(newLat)
        setLng(newLng)
        if (mapRef) mapRef.panTo({ lat: newLat, lng: newLng })
      }
    } catch {
      showMessage('Error fetching place details', 'error')
    }
    hideLoading()
  }

  const geocodeBeforeSubmit = async () => {
    if ((!lat || !lng) && address) {
      try {
        const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
        const res = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${key}`)
        if (res.data.results && res.data.results.length) {
          setLat(res.data.results[0].geometry.location.lat)
          setLng(res.data.results[0].geometry.location.lng)
          if (mapRef) mapRef.panTo({ lat: res.data.results[0].geometry.location.lat, lng: res.data.results[0].geometry.location.lng })
        }
      } catch {}
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    showLoading()
    await geocodeBeforeSubmit()
    try {
      const payload = {
        name,
        address,
        lat: lat ? parseFloat(lat) : 0,
        lng: lng ? parseFloat(lng) : 0,
        isOurSite
      }
      const res = await api.post('/site/create', payload)
      if (res.data && res.data.siteId) {
        showMessage('Site created successfully', 'success')
        navigate('/dashboard')
      } else {
        showMessage('Failed to create site', 'error')
      }
    } catch {
      showMessage('Error creating site', 'error')
    }
    hideLoading()
  }

  if (loadError) {
    return (
      <div className="site-page-container">
        <h2>Create Site</h2>
        <p>Map failed to load.</p>
      </div>
    )
  }

  return (
    <div className="site-page-container">
      <h2 className="site-title">Create Site</h2>
      <form onSubmit={handleSubmit} className="site-form">
        <div className="site-form-group">
          <label className="site-label">Site Name</label>
          <input
            className="site-input"
            value={name}
            onChange={e => setName(e.target.value)}
            required
          />
        </div>
        <div className="site-form-group">
          <label className="site-label">Address</label>
          <input
            className="site-input"
            value={address}
            onChange={handleAddressChange}
            required
          />
          {predictions.length > 0 && (
            <ul className="site-autocomplete-list">
              {predictions.map(p => (
                <li
                  key={p.place_id}
                  className="site-autocomplete-item"
                  onClick={() => handlePredictionClick(p)}
                >
                  {p.description}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="site-form-group-checkbox">
          <label className="site-label-inline">Is Our Site</label>
          <input
            type="checkbox"
            checked={isOurSite}
            onChange={() => setIsOurSite(!isOurSite)}
          />
        </div>
        <button
          type="button"
          className="site-btn"
          onClick={fetchCurrentLocation}
        >
          Get Current Location
        </button>
        {isLoaded && (
          <div className="site-map-container">
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={{ lat: Number(lat), lng: Number(lng) }}
              zoom={12}
              onLoad={onLoadMap}
              onUnmount={onUnmountMap}
              options={{ disableDefaultUI: false, zoomControl: true }}
            >
              <Marker
                position={{ lat: Number(lat), lng: Number(lng) }}
                draggable
                onDragEnd={handleMarkerDragEnd}
              />
            </GoogleMap>
          </div>
        )}
        <button type="submit" className="site-btn site-submit">
          Create Site
        </button>
      </form>
    </div>
  )
}

export default SiteCreate